// Time Report Calculations and Aggregations
import {
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameDay,
  startOfMonth,
  endOfMonth,
  differenceInDays,
} from 'date-fns';
import { parseUTCTimestamp } from './dateUtils';
import {
  TimeLog,
  StaffReport,
  DayShift,
  WeeklyBreakdown,
  MonthlyBreakdown,
  ReportSummary,
} from '../types/timeReport';

/**
 * Calculate hours worked for a shift (1-hour default if incomplete)
 */
export const calculateHours = (clockIn: string, clockOut: string | null): number => {
  if (!clockOut) return 1.0; // Default for incomplete shifts (forgot to clock out)

  const start = parseUTCTimestamp(clockIn);
  const end = parseUTCTimestamp(clockOut);
  return (end.getTime() - start.getTime()) / (1000 * 60 * 60);
};

/**
 * Group time logs by staff member
 */
export const groupByStaff = (logs: TimeLog[]): Map<string, TimeLog[]> => {
  const grouped = new Map<string, TimeLog[]>();

  logs.forEach((log) => {
    const staffId = log.staff_id;
    if (!grouped.has(staffId)) {
      grouped.set(staffId, []);
    }
    grouped.get(staffId)!.push(log);
  });

  return grouped;
};

/**
 * Calculate staff report from time logs
 */
export const calculateStaffReport = (staffId: string, logs: TimeLog[]): StaffReport => {
  const staffLogs = logs.filter((log) => log.staff_id === staffId);

  if (staffLogs.length === 0) {
    return {
      staff_id: staffId,
      staff_name: '',
      staff_role: '',
      staff_photo: null,
      total_hours: 0,
      days_worked: 0,
      avg_hours: 0,
      shift_count: 0,
      incomplete_count: 0,
      shifts: [],
    };
  }

  // Get unique work dates
  const workDates = new Set<string>();
  staffLogs.forEach((log) => {
    try {
      if (log.clock_in_time) {
        const date = format(parseUTCTimestamp(log.clock_in_time), 'yyyy-MM-dd');
        workDates.add(date);
      }
    } catch (err) {
      // Skip invalid dates
      console.warn('Invalid date in staff log:', log.clock_in_time, err);
    }
  });

  const totalHours = staffLogs.reduce((sum, log) => {
    const hours = log.hours_worked || calculateHours(log.clock_in_time, log.clock_out_time);
    return sum + hours;
  }, 0);

  const incompleteCount = staffLogs.filter((log) => log.needs_review).length;

  return {
    staff_id: staffId,
    staff_name: staffLogs[0].staff_name || '',
    staff_role: staffLogs[0].staff_role || '',
    staff_photo: staffLogs[0].staff_photo || null,
    total_hours: totalHours,
    days_worked: workDates.size,
    avg_hours: workDates.size > 0 ? totalHours / workDates.size : 0,
    shift_count: staffLogs.length,
    incomplete_count: incompleteCount,
    shifts: staffLogs,
  };
};

/**
 * Generate staff reports for all staff members in logs
 */
export const generateStaffReports = (logs: TimeLog[]): StaffReport[] => {
  const staffMap = groupByStaff(logs);
  const reports: StaffReport[] = [];

  staffMap.forEach((staffLogs, staffId) => {
    reports.push(calculateStaffReport(staffId, logs));
  });

  // Sort by total hours descending
  return reports.sort((a, b) => b.total_hours - a.total_hours);
};

/**
 * Calculate overall summary from all logs
 */
export const calculateReportSummary = (logs: TimeLog[]): ReportSummary => {
  const totalHours = logs.reduce((sum, log) => {
    const hours = log.hours_worked || calculateHours(log.clock_in_time, log.clock_out_time);
    return sum + hours;
  }, 0);

  const activeShifts = logs.filter(
    (log) => log.status === 'clocked_in' && !log.clock_out_time
  ).length;

  const incompleteShifts = logs.filter((log) => log.needs_review).length;

  // Get unique work dates
  const workDates = new Set<string>();
  logs.forEach((log) => {
    try {
      if (log.clock_in_time) {
        const date = format(parseUTCTimestamp(log.clock_in_time), 'yyyy-MM-dd');
        workDates.add(date);
      }
    } catch (err) {
      // Skip invalid dates
      console.warn('Invalid date in log:', log.clock_in_time, err);
    }
  });

  return {
    total_hours: totalHours,
    avg_hours_per_day: workDates.size > 0 ? totalHours / workDates.size : 0,
    active_shifts: activeShifts,
    incomplete_shifts: incompleteShifts,
    total_shifts: logs.length,
    days_worked: workDates.size,
  };
};

/**
 * Generate daily shifts for a date range
 */
export const generateDailyShifts = (
  logs: TimeLog[],
  startDate: Date,
  endDate: Date
): DayShift[] => {
  const days = eachDayOfInterval({ start: startDate, end: endDate });

  return days.map((day) => {
    const dayShifts = logs.filter((log) => {
      try {
        if (log.clock_in_time) {
          return isSameDay(parseUTCTimestamp(log.clock_in_time), day);
        }
        return false;
      } catch (err) {
        console.warn('Invalid date in shift filter:', log.clock_in_time, err);
        return false;
      }
    });

    const totalHours = dayShifts.reduce((sum, log) => {
      const hours = log.hours_worked || calculateHours(log.clock_in_time, log.clock_out_time);
      return sum + hours;
    }, 0);

    return {
      date: day,
      dateString: format(day, 'yyyy-MM-dd'),
      shifts: dayShifts,
      total_hours: totalHours,
      is_off: dayShifts.length === 0,
    };
  });
};

/**
 * Generate weekly breakdown
 */
export const generateWeeklyBreakdown = (
  logs: TimeLog[],
  weekStart: Date
): WeeklyBreakdown => {
  const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 }); // Monday start
  const days = generateDailyShifts(logs, weekStart, weekEnd);

  const totalHours = days.reduce((sum, day) => sum + day.total_hours, 0);
  const daysWorked = days.filter((day) => !day.is_off).length;

  return {
    week_start: weekStart,
    week_end: weekEnd,
    week_label: `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'd, yyyy')}`,
    days,
    total_hours: totalHours,
    days_worked: daysWorked,
  };
};

/**
 * Generate monthly breakdown with weeks
 */
export const generateMonthlyBreakdown = (
  logs: TimeLog[],
  month: number,
  year: number
): MonthlyBreakdown => {
  const monthStart = startOfMonth(new Date(year, month, 1));
  const monthEnd = endOfMonth(monthStart);

  // Get all weeks in the month
  const weeks: WeeklyBreakdown[] = [];
  let currentWeekStart = startOfWeek(monthStart, { weekStartsOn: 1 });

  while (currentWeekStart <= monthEnd) {
    // Capture the current week start in a const to avoid closure issues
    const weekStartCapture = currentWeekStart;
    const weekEndCapture = endOfWeek(weekStartCapture, { weekStartsOn: 1 });

    const weekLogs = logs.filter((log) => {
      try {
        if (log.clock_in_time) {
          const logDate = parseUTCTimestamp(log.clock_in_time);
          return logDate >= weekStartCapture && logDate <= weekEndCapture;
        }
        return false;
      } catch (err) {
        console.warn('Invalid date in monthly breakdown:', log.clock_in_time, err);
        return false;
      }
    });

    weeks.push(generateWeeklyBreakdown(weekLogs, weekStartCapture));
    currentWeekStart = new Date(currentWeekStart);
    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
  }

  const totalHours = weeks.reduce((sum, week) => sum + week.total_hours, 0);
  const daysWorked = weeks.reduce((sum, week) => sum + week.days_worked, 0);

  return {
    month,
    year,
    month_label: format(monthStart, 'MMMM yyyy'),
    weeks,
    total_hours: totalHours,
    days_worked: daysWorked,
    avg_hours_per_day: daysWorked > 0 ? totalHours / daysWorked : 0,
  };
};

/**
 * Validate date range (max 6 months)
 */
export const validateDateRange = (startDate: Date, endDate: Date): boolean => {
  const diffDays = differenceInDays(endDate, startDate);
  return diffDays >= 0 && diffDays <= 180; // 6 months = ~180 days
};

/**
 * Format hours for display (e.g., "8.5h" or "8h 30m")
 */
export const formatHours = (hours: number, detailed: boolean = false): string => {
  if (!detailed) {
    return `${hours.toFixed(1)}h`;
  }

  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);

  if (minutes === 0) {
    return `${wholeHours}h`;
  }

  return `${wholeHours}h ${minutes}m`;
};
